import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

import { ref, onMounted, watch } from 'vue';
import ImageCropper from '@/components/ImageCropper/ImageCropper.vue';
import { uploadImage } from '@/services/common/common.service';
import { getWc3Maps } from '@/services/wc3/wc3.service';
import type { Wc3Map } from '@/services/wc3/wc3.model';
import type { CreateGeoGuesserRequest } from '@/services/geoGuesser/geoGuesser.model';
import { compressImage } from '@/utils/image.util';

interface Props {
  initialData?: {
    id: number;
    mapId: number;
    images: {
      small: string;
      medium: string;
      large: string;
    };
  };
  onSubmit: (payload: CreateGeoGuesserRequest) => Promise<void>;
  loading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GeoGuesserForm',
  props: {
    initialData: {},
    onSubmit: { type: Function },
    loading: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const maps = ref<Wc3Map[]>([]);
const selectedMap = ref<number | null>(null);

const smallImage = ref<string | null>(null);
const mediumImage = ref<string | null>(null);
const largeImage = ref<string | null>(null);

const smallFile = ref<File | null>(null);
const mediumFile = ref<File | null>(null);
const largeFile = ref<File | null>(null);

const pageLoading = ref(true);
const errorMessage = ref('');

onMounted(async () => {
  try {
    maps.value = await getWc3Maps();
    if (props.initialData) fillFromInitial(props.initialData);
  } catch (err) {
    console.error(err);
    errorMessage.value = 'Failed to load maps.';
  } finally {
    pageLoading.value = false;
  }
});

watch(
  () => props.initialData,
  (val) => val && fillFromInitial(val)
);

function fillFromInitial(data: any) {
  selectedMap.value = data.mapId;
  smallImage.value = data.images.small;
  mediumImage.value = data.images.medium;
  largeImage.value = data.images.large;
}

function onSmallSelected(base64: string, file?: File) {
  smallImage.value = base64;
  if (file) smallFile.value = file;
}

function onMediumSelected(base64: string, file?: File) {
  mediumImage.value = base64;
  if (file) mediumFile.value = file;
}

function onLargeSelected(base64: string, file?: File) {
  largeImage.value = base64;
  if (file) largeFile.value = file;
}

async function handleSubmit() {
  errorMessage.value = '';

  if (!selectedMap.value) {
    errorMessage.value = 'Please select a map.';
    return;
  }

  async function uploadIfNeeded(file: File | null): Promise<number | undefined> {
    if (!file) return undefined;
    const compressed = await compressImage(file, 1000, 0.8);
    const res = await uploadImage(compressed);
    return res.imageId;
  }

  const smallImageId = await uploadIfNeeded(smallFile.value);
  const mediumImageId = await uploadIfNeeded(mediumFile.value);
  const largeImageId = await uploadIfNeeded(largeFile.value);

  const payload: CreateGeoGuesserRequest = {
    mapId: selectedMap.value,
    smallImageId,
    mediumImageId,
    largeImageId,
  };

  try {
    await props.onSubmit(payload);
  } catch (err: any) {
    console.error(err);
    errorMessage.value = err?.message || 'Failed to save GeoGuesser.';
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!

  return (pageLoading.value)
    ? (_openBlock(), _createBlock(_component_v_progress_circular, {
        key: 0,
        indeterminate: "",
        class: "mx-auto my-6"
      }))
    : (_openBlock(), _createBlock(_component_v_form, {
        key: 1,
        onSubmit: _withModifiers(handleSubmit, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_select, {
            modelValue: selectedMap.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedMap).value = $event)),
            items: maps.value,
            "item-title": "name",
            "item-value": "id",
            label: "Warcraft 3 Map",
            outlined: "",
            required: ""
          }, null, 8, ["modelValue", "items"]),
          _createVNode(ImageCropper, {
            label: "Small Image",
            modelValue: smallImage.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((smallImage).value = $event)),
            onChange: onSmallSelected,
            aspectRatio: 5 / 3
          }, null, 8, ["modelValue"]),
          _createVNode(ImageCropper, {
            label: "Medium Image",
            modelValue: mediumImage.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((mediumImage).value = $event)),
            onChange: onMediumSelected,
            aspectRatio: 5 / 3
          }, null, 8, ["modelValue"]),
          _createVNode(ImageCropper, {
            label: "Large Image",
            modelValue: largeImage.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((largeImage).value = $event)),
            onChange: onLargeSelected,
            aspectRatio: 5 / 3
          }, null, 8, ["modelValue"]),
          (errorMessage.value)
            ? (_openBlock(), _createBlock(_component_v_alert, {
                key: 0,
                type: "error",
                variant: "tonal",
                class: "mt-4"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(errorMessage.value), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_v_btn, {
            type: "submit",
            color: "primary",
            block: "",
            class: "mt-4",
            loading: __props.loading
          }, {
            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
              _createTextVNode(" Save GeoGuesser ", -1)
            ]))]),
            _: 1
          }, 8, ["loading"])
        ]),
        _: 1
      }))
}
}

})